import { useEffect } from "react";
import {
  Crown, ArrowRight, Calendar, Sparkles, Check, X, TrendingUp, Trophy,
  Calculator, Zap, ChevronRight, Users, Star, AlertTriangle, Target,
} from "lucide-react";

const Stat = ({ value, label, kicker }) => (
  <div className="text-center">
    {kicker && <p className="text-[10px] text-amber-300 font-mono uppercase tracking-[2px] mb-1">{kicker}</p>}
    <p className="text-3xl sm:text-5xl font-black text-white leading-none">{value}</p>
    <p className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider mt-2">{label}</p>
  </div>
);

const PlanCard = ({ tier, name, price, total, ghl, perks, isEmpire, description }) => (
  <div
    data-testid={`evolucion-plan-${tier.toLowerCase()}`}
    className={`relative rounded-2xl overflow-hidden border transition-all hover:-translate-y-1 ${
      isEmpire
        ? "bg-gradient-to-br from-amber-400/15 via-amber-500/10 to-amber-400/15 border-amber-400/60 shadow-[0_20px_60px_rgba(212,175,55,0.25)]"
        : "bg-slate-900/40 border-slate-700/50 hover:border-slate-500/60"
    }`}
  >
    {isEmpire && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
        <span className="inline-flex items-center gap-1 bg-amber-400 text-slate-900 text-[10px] font-black uppercase tracking-[2px] px-3 py-1.5 rounded-full">
          <Star className="h-3 w-3 fill-current" /> Solución Máxima
        </span>
      </div>
    )}
    <div className={`p-6 ${isEmpire ? "pt-8" : ""}`}>
      <p className={`text-[10px] font-mono uppercase tracking-[3px] mb-2 ${isEmpire ? "text-amber-400" : "text-slate-500"}`}>
        Plan · {tier}
      </p>
      <h3 className={`text-2xl sm:text-3xl font-black mb-3 ${isEmpire ? "text-amber-200" : "text-white"}`}>{name}</h3>
      <p className="text-xs text-slate-400 leading-relaxed mb-5 min-h-[3rem]">{description}</p>

      {/* Precio base */}
      <div className="mb-4">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Precio del plan</p>
        <p className={`text-3xl sm:text-4xl font-black ${isEmpire ? "text-amber-300" : "text-white"} leading-none`}>{price}</p>
        <p className="text-[11px] text-slate-500 mt-0.5">USD · pago único</p>
      </div>

      {/* Costo GHL */}
      <div className={`rounded-xl p-3 mb-4 border ${
        isEmpire ? "bg-emerald-500/10 border-emerald-400/30" : "bg-rose-500/5 border-rose-400/20"
      }`}>
        <p className={`text-[10px] uppercase tracking-wider font-mono font-bold ${isEmpire ? "text-emerald-400" : "text-rose-400"}`}>
          Plataforma GHL
        </p>
        <p className={`text-sm font-bold ${isEmpire ? "text-emerald-300" : "text-rose-300"}`}>
          {ghl}
        </p>
      </div>

      {/* Total real */}
      <div className={`rounded-xl p-4 mb-5 border-2 ${
        isEmpire ? "bg-amber-400/10 border-amber-400/40" : "bg-slate-800/60 border-slate-600/40"
      }`}>
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-mono mb-1">Costo total real</p>
        <p className={`text-4xl font-black ${isEmpire ? "text-amber-300" : "text-white"} leading-none`}>{total}</p>
      </div>

      {/* Perks */}
      <ul className="space-y-2 text-sm">
        {perks.map(p => (
          <li key={p} className="flex gap-2 text-slate-300">
            <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${isEmpire ? "text-amber-400" : "text-slate-500"}`} />
            <span className="leading-snug">{p}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const PLANES = [
  {
    tier: "I", name: "Starter", price: "$697",
    description: "Primer paso. Un módulo para probar el modelo y empezar a generar ingresos.",
    ghl: "+ $1,000 adicionales (1 módulo)",
    total: "$1,697",
    isEmpire: false,
    perks: [
      "1 secreto / módulo de negocio",
      "Acceso a la red Club Viajemos",
      "Soporte del equipo base",
    ],
  },
  {
    tier: "II", name: "Dúo", price: "$997",
    description: "Dos módulos activos para mayor diversificación de ingresos desde el inicio.",
    ghl: "+ $2,000 adicionales (2 módulos)",
    total: "$2,997",
    isEmpire: false,
    perks: [
      "2 secretos / módulos de negocio",
      "Acceso a la red Club Viajemos",
      "Mayor diversificación de ingresos",
    ],
  },
  {
    tier: "III", name: "Profesional", price: "$1,497",
    description: "Cuatro módulos activos para agentes serios que quieren maximizar sus líneas de negocio.",
    ghl: "+ $4,000 adicionales (4 módulos)",
    total: "$5,497",
    isEmpire: false,
    perks: [
      "4 secretos / módulos de negocio",
      "Múltiples fuentes de ingreso activas",
      "Acceso completo a red y capacitación",
    ],
  },
  {
    tier: "IV", name: "Empire", price: "$4,995",
    description: "Todo incluido. Conocimiento + plataforma + liderazgo. Un negocio llave en mano desde el día 1.",
    ghl: "INCLUIDA — sin costo adicional",
    total: "$4,995",
    isEmpire: true,
    perks: [
      "Todos los secretos y módulos de negocio",
      "https://app.viajemos.us incluida (sin costo adicional)",
      "Acceso directo a Carlos Fernández (Miami)",
      "Herramientas adicionales exclusivas",
      "Sistema de captación de leads desde el día 1",
    ],
  },
];

const EvolucionSistemaPage = () => {
  useEffect(() => {
    document.title = "La Evolución del Sistema · ClubViajemos";
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a14] via-[#0d0d1c] to-[#0a0a14] text-white" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }} data-testid="evolucion-page">
      {/* TOP BANNER */}
      <div className="bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 text-slate-900 text-center py-2 text-xs sm:text-sm font-black tracking-wider sticky top-0 z-40">
        <Crown className="inline h-3.5 w-3.5 mr-1.5 mb-0.5" />
        DOCUMENTO ESTRATÉGICO PARA AGENTES · 2025
      </div>

      {/* HERO */}
      <section className="relative px-4 pt-16 pb-12 sm:pt-24 sm:pb-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-amber-400/10 rounded-full blur-[180px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-violet-400/8 rounded-full blur-[160px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto text-center">
          <p className="inline-block bg-amber-400/15 text-amber-300 text-[11px] uppercase tracking-[3px] px-4 py-1.5 rounded-full font-mono mb-6 border border-amber-400/30">
            La evolución del sistema
          </p>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight mb-5">
            De prueba y error<br />
            <span className="text-amber-300">a un sistema que funciona</span>
          </h1>

          <p className="text-base sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-2">
            27 webinars. Miles de horas de aprendizaje. Una sola conclusión:
          </p>
          <p className="text-lg sm:text-2xl text-amber-200 font-bold max-w-3xl mx-auto mb-10">
            La simplicidad vende, y el sistema correcto lo hace todo.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto py-7 border-y border-white/10">
            <Stat value="27" label="Webinars perfeccionando" />
            <Stat value="5,400+" label="Agencias en la red" />
            <Stat value="2x" label="Oportunidades/semana" />
          </div>

          {/* CTAs */}
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/precios"
              data-testid="evolucion-cta-precios"
              className="inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-amber-400 to-amber-300 hover:from-amber-300 hover:to-amber-400 text-slate-900 font-black px-7 py-4 rounded-xl uppercase tracking-wider text-sm hover:scale-[1.03] transition-transform shadow-[0_15px_50px_rgba(212,175,55,0.4)]"
            >
              <Crown className="h-5 w-5" /> Ver planes y precios
            </a>
            <a
              href="#comparativa"
              className="inline-flex items-center justify-center gap-2.5 bg-white/5 border border-white/20 hover:bg-white/10 text-white font-bold px-7 py-4 rounded-xl uppercase tracking-wider text-sm transition-all"
            >
              Ver la matemática <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ANTES vs HOY */}
      <section className="px-4 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[11px] text-amber-400 font-mono tracking-[3px] uppercase mb-2">El antes y el después</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
              Lo que aprendimos de <span className="text-amber-300">27 webinars mensuales</span>
            </h2>
            <p className="text-slate-300 text-sm sm:text-base mt-3 max-w-2xl mx-auto">
              Y cómo lo transformamos en un sistema reproducible cada semana.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5" data-testid="evolucion-antes-despues">
            {/* ANTES */}
            <div className="rounded-2xl bg-gradient-to-br from-rose-950/40 to-slate-900/60 border-2 border-rose-500/30 p-7 sm:p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-rose-500/20 text-rose-300 text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider border border-rose-400/30">Sistema viejo</span>
              </div>
              <p className="text-[11px] font-mono uppercase tracking-[3px] text-rose-400 mb-1">Antes</p>
              <h3 className="text-2xl sm:text-3xl font-black mb-6">Sistema mensual</h3>
              <ul className="space-y-3 text-sm">
                {[
                  "1 sola oportunidad al mes",
                  "30 días de espera entre ciclos",
                  "Dependía del calendario: viernes, fútbol, lunes...",
                  "Había que asistir 4 días completos o no entendías",
                  "$597 con todo: tan barato que generaba desconfianza",
                  "Sistema frágil, trabajando casi con la suerte",
                ].map(item => (
                  <li key={item} className="flex gap-3 text-slate-300">
                    <X className="h-4 w-4 text-rose-400 flex-shrink-0 mt-0.5" />
                    <span className="leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* HOY */}
            <div className="rounded-2xl bg-gradient-to-br from-emerald-950/40 to-slate-900/60 border-2 border-emerald-500/40 p-7 sm:p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-emerald-500/20 text-emerald-300 text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider border border-emerald-400/30">Activo hoy</span>
              </div>
              <p className="text-[11px] font-mono uppercase tracking-[3px] text-emerald-400 mb-1">Hoy</p>
              <h3 className="text-2xl sm:text-3xl font-black mb-6">Sistema semanal en loop</h3>
              <ul className="space-y-3 text-sm">
                {[
                  "Miércoles 8:45 pm y sábados 10:30 am (hora Miami)",
                  "Si no puedes el miércoles, tienes el sábado",
                  "Los dos webinars se complementan entre sí",
                  "4 planes con precios claros y diferenciados",
                  "Sistema predecible, semana a semana",
                ].map(item => (
                  <li key={item} className="flex gap-3 text-slate-200">
                    <Check className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex items-center gap-2 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 rounded-lg p-3">
                <Calendar className="h-4 w-4 flex-shrink-0" />
                <span><strong>Próximo webinar:</strong> consulta el calendario semanal en clubviajemos.com</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOS 4 PLANES */}
      <section className="px-4 py-12 sm:py-16 bg-gradient-to-b from-transparent via-amber-400/[0.02] to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[11px] text-amber-400 font-mono tracking-[3px] uppercase mb-2">Los 4 planes</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-3">
              Elige tu nivel de <span className="text-amber-300">compromiso e ingresos</span>
            </h2>
            <p className="text-slate-300 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
              La plataforma <strong className="text-amber-300">https://app.viajemos.us (GHL)</strong> vale <strong>$1,000 USD por módulo</strong>.
              A mayor plan, mayor costo de agregar GHL — porque hay más módulos que activar. En Empire ya viene todo incluido sin costo adicional.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLANES.map(p => <PlanCard key={p.name} {...p} />)}
          </div>
        </div>
      </section>

      {/* ¿POR QUÉ GHL CAMBIA TODO? */}
      <section className="px-4 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-3xl bg-gradient-to-br from-slate-900/80 to-slate-950/80 border border-amber-400/30 p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-amber-400/15 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-14 h-14 rounded-2xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
                  <Zap className="h-7 w-7 text-amber-300" />
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[3px] text-amber-400">La verdad incómoda</p>
                  <h2 className="text-2xl sm:text-3xl font-black">¿Por qué https://app.viajemos.us cambia todo?</h2>
                </div>
              </div>

              <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-5">
                Los datos de 27 webinars revelaron una verdad incómoda:
                <span className="text-amber-200 font-bold"> incluso con todos los módulos</span>, sin un sistema que permita comprar y gestionar leads, <strong>el 95% de los agentes no logra resultados de crecimiento constante</strong>.
              </p>

              <div className="grid sm:grid-cols-3 gap-4 my-7">
                <div className="text-center p-5 rounded-xl bg-rose-500/10 border border-rose-400/30">
                  <p className="text-4xl font-black text-rose-300 leading-none">95%</p>
                  <p className="text-xs text-slate-300 mt-2 leading-tight">de agentes sin sistema de leads<br />no logran crecimiento constante</p>
                </div>
                <div className="text-center p-5 rounded-xl bg-amber-500/10 border border-amber-400/30">
                  <p className="text-4xl font-black text-amber-300 leading-none">5%</p>
                  <p className="text-xs text-slate-300 mt-2 leading-tight">avanzan a base de fuerza<br />de voluntad pura</p>
                </div>
                <div className="text-center p-5 rounded-xl bg-emerald-500/10 border border-emerald-400/30">
                  <p className="text-4xl font-black text-emerald-300 leading-none">100%</p>
                  <p className="text-xs text-slate-300 mt-2 leading-tight">de los que activan GHL<br />tienen leads diarios</p>
                </div>
              </div>

              <p className="text-base sm:text-lg text-center text-white font-bold">
                Empire resuelve eso. <span className="text-amber-300">GHL incluida desde el día 1.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARATIVA — La matemática habla sola */}
      <section id="comparativa" className="px-4 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[11px] text-amber-400 font-mono tracking-[3px] uppercase mb-2">La matemática habla sola</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
              Empire no es el plan más caro.<br />
              <span className="text-amber-300">Es el único completo.</span>
            </h2>
            <p className="text-slate-300 text-sm sm:text-base mt-3 max-w-2xl mx-auto leading-relaxed">
              Con Starter, Dúo o Profesional <strong>más GHL</strong> terminas pagando más que Empire — y aun así no tienes el liderazgo de Carlos Fernández ni las herramientas exclusivas.
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden border border-white/10 bg-slate-900/40">
            <div className="grid grid-cols-12 bg-slate-800/60 text-[10px] sm:text-xs font-mono uppercase tracking-wider text-slate-400 font-bold">
              <div className="col-span-4 p-4">Plan</div>
              <div className="col-span-3 p-4 text-right">Precio plan</div>
              <div className="col-span-2 p-4 text-right">+ GHL</div>
              <div className="col-span-3 p-4 text-right text-amber-400">Total real</div>
            </div>
            {[
              { name: "Starter", price: "$697", ghl: "+$1,000", total: "$1,697", empire: false },
              { name: "Dúo", price: "$997", ghl: "+$2,000", total: "$2,997", empire: false },
              { name: "Profesional", price: "$1,497", ghl: "+$4,000", total: "$5,497", empire: false, highlight: true },
              { name: "Empire ★", price: "$4,995", ghl: "INCLUIDA", total: "$4,995", empire: true },
            ].map((row, i) => (
              <div
                key={row.name}
                className={`grid grid-cols-12 text-sm border-t border-white/5 ${
                  row.empire ? "bg-gradient-to-r from-amber-400/15 via-amber-500/10 to-amber-400/15" : ""
                }`}
              >
                <div className={`col-span-4 p-4 font-bold ${row.empire ? "text-amber-300" : "text-white"}`}>{row.name}</div>
                <div className="col-span-3 p-4 text-right text-slate-300">{row.price}</div>
                <div className={`col-span-2 p-4 text-right ${row.empire ? "text-emerald-300 font-bold" : row.highlight ? "text-rose-300" : "text-slate-400"}`}>{row.ghl}</div>
                <div className={`col-span-3 p-4 text-right font-black ${row.empire ? "text-amber-300" : row.highlight ? "text-rose-300" : "text-white"}`}>{row.total}</div>
              </div>
            ))}
          </div>

          {/* Ahorro callout */}
          <div className="mt-6 rounded-2xl bg-gradient-to-r from-emerald-500/15 via-emerald-400/10 to-emerald-500/15 border border-emerald-400/30 p-6 text-center">
            <p className="text-[11px] font-mono uppercase tracking-[3px] text-emerald-400 mb-2">El número que importa</p>
            <p className="text-3xl sm:text-4xl font-black text-emerald-300 mb-2">
              Empire es $500 USD más barato que Profesional + GHL
            </p>
            <p className="text-sm text-slate-300 max-w-2xl mx-auto leading-relaxed">
              Y además incluye: acceso directo a Carlos, herramientas exclusivas, sistema de leads y módulos completos.
              <strong className="text-emerald-200"> Más por menos.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="px-4 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-br from-amber-500/15 via-slate-900/60 to-slate-950 border-2 border-amber-400/40 p-8 sm:p-12 text-center relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.15),transparent_60%)] pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border-2 border-amber-400 bg-amber-400/10 mb-5">
              <Crown className="h-10 w-10 text-amber-300" />
            </div>
            <p className="text-[11px] text-amber-300 font-mono tracking-[3px] uppercase mb-2">Tu decisión</p>
            <h3 className="text-3xl sm:text-4xl font-black mb-4 leading-tight">
              ¿Quieres avanzar dos veces más rápido?
            </h3>
            <p className="text-slate-300 text-base leading-relaxed mb-7 max-w-xl mx-auto">
              Únete a las <strong className="text-amber-200">+5,400 agencias</strong> que ya operan con el sistema semanal y elige el plan que se ajusta a tu nivel de compromiso.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/precios"
                data-testid="evolucion-cta-final"
                className="inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-amber-400 to-amber-300 hover:from-amber-300 hover:to-amber-400 text-slate-900 font-black px-8 py-4 rounded-xl uppercase tracking-wider text-base hover:scale-[1.03] transition-transform shadow-[0_15px_50px_rgba(212,175,55,0.4)]"
              >
                <Crown className="h-5 w-5" /> Ver planes y elegir el mío
              </a>
            </div>
            <p className="text-xs text-slate-400 mt-6 flex items-center gap-1.5 justify-center">
              <Calendar className="h-3 w-3" /> Webinars: miércoles 8:45 pm &amp; sábados 10:30 am (hora Miami)
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 px-4 text-center text-xs text-slate-500 border-t border-white/5">
        <p>&copy; 2026 Club Viajemos / Travel Empire</p>
        <p className="mt-1">+5,400 agencias · Carlos Miami Fernández · CEO ClubViajemos</p>
        <p className="mt-3">
          <a href="/" className="text-amber-400 hover:underline">← Volver al sitio principal</a>
        </p>
      </footer>
    </div>
  );
};

export default EvolucionSistemaPage;
